"use client";

import { LocationAutocomplete } from "@/components/ui/create-event/LocationAutocomplete";
import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchInput } from "@/fetch-ui/FetchInput";
import { FormattedLocation } from "@/hooks/useLocationSearch";
import { AnimatePresence } from "motion/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { LOCATION_LIMIT, NAME_MAX_LENGTH } from "../../_logic/constants";
import { selectedFromFormatted } from "../../_logic/location-utils";
import { hasEventLocation, hasEventName } from "../../_logic/step-completion";
import { useSubPhase } from "../../_hooks/use-sub-phase";
import { AboutYourEventSubPhase, StepProps } from "../../_types/wizard-types";
import { SubPhasePanel } from "./SubPhasePanel";
import { SuggestedLocations } from "./SuggestedLocations";

const ABOUT_PHASES: readonly AboutYourEventSubPhase[] = ["name", "location"];

/**
 * Step 1: "About your event" — event name and location chained as two screens via local `subPhase`
 * state, following the established subPhase pattern (e.g. `GuestroomsStep`). Each screen ports one of
 * v2's original standalone steps (`EventNameStep`, `LocationStep`) content-for-content. Expected
 * headcount is its own step (`HeadcountStep`), positioned after Dates rather than here, so Dates can
 * sit directly after Location.
 */
export const AboutYourEventStep = ({ formData, dispatch, onNext, onBack }: StepProps) => {
  // Hooks
  const {
    phase: subPhase,
    direction: subDirection,
    goTo,
    goBack: handleSubBack,
  } = useSubPhase(ABOUT_PHASES, "name", onBack);
  const nameInputRef = useRef<HTMLInputElement>(null);

  // Derived values
  const canContinueName = hasEventName(formData);
  const canContinueLocation = hasEventLocation(formData);
  // A location can be `pending` while its place-details fetch resolves the real city — surfaced as
  // a loading state on Continue (rather than just `disabled`) so a slow lookup reads as "still
  // working" instead of looking broken/unresponsive.
  const isLocationResolving = formData.formattedLocations.some((loc) => loc.pending);
  const selectedLocations = useMemo(
    () => selectedFromFormatted(formData.formattedLocations),
    [formData.formattedLocations]
  );

  // Handlers
  const handleLocationChange = useCallback(
    (_selectedOptions: string[], formattedLocations: FormattedLocation[]) => {
      dispatch({ type: "SET_LOCATION", payload: { formattedLocations } });
    },
    [dispatch]
  );

  const goToLocation = useCallback(() => goTo("location"), [goTo]);

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && canContinueName) goToLocation();
  };

  // Effects
  useEffect(() => {
    if (subPhase !== "name") return;
    const timer = setTimeout(() => nameInputRef.current?.focus(), 400);
    return () => clearTimeout(timer);
  }, [subPhase]);

  // Render
  return (
    <div className="flex flex-col h-[420px]">
      <AnimatePresence mode="wait" custom={subDirection}>
        {subPhase === "name" && (
          <SubPhasePanel phaseKey="name" direction={subDirection} className="flex flex-col h-full">
            <h1 className="text-3xl md:text-4xl font-semibold text-gray-900 tracking-tight">
              What is the name of your event?
            </h1>
            <div className="flex flex-col gap-2 mt-10">
              <FetchInput
                ref={nameInputRef}
                type="text"
                placeholder="e.g. Annual company retreat"
                value={formData.name}
                maxLength={NAME_MAX_LENGTH}
                onChange={(e) => dispatch({ type: "SET_NAME", payload: e.target.value })}
                onKeyDown={handleNameKeyDown}
                className="text-base py-3"
              />
              <span className="text-xs font-medium text-gray-400 text-right">
                {formData.name.length}/{NAME_MAX_LENGTH}
              </span>
            </div>
            <div className="flex gap-3 mt-auto">
              {onBack && (
                <FetchButton variant="outline" color="neutral" size="lg" onClick={onBack} className="flex-1">
                  Back
                </FetchButton>
              )}
              <FetchButton
                variant="primary"
                size="lg"
                onClick={goToLocation}
                disabled={!canContinueName}
                className="flex-1"
              >
                Continue
              </FetchButton>
            </div>
          </SubPhasePanel>
        )}

        {subPhase === "location" && (
          <SubPhasePanel phaseKey="location" direction={subDirection} className="flex flex-col h-full">
            <h1 className="text-3xl md:text-4xl font-semibold text-gray-900 tracking-tight">
              Where will your event take place?
            </h1>
            <div className="mt-10">
              <LocationAutocomplete
                searchType="address"
                selectedLocations={selectedLocations}
                formattedLocations={formData.formattedLocations}
                onLocationChange={handleLocationChange}
                limit={LOCATION_LIMIT}
                showPills
                placeholder="Search for a city, address, or destination"
              />
            </div>
            <SuggestedLocations formData={formData} dispatch={dispatch} limit={LOCATION_LIMIT} />
            <div className="flex gap-3 mt-auto">
              <FetchButton variant="outline" color="neutral" size="lg" onClick={handleSubBack} className="flex-1">
                Back
              </FetchButton>
              <FetchButton
                variant="primary"
                size="lg"
                onClick={onNext}
                disabled={!canContinueLocation}
                loading={isLocationResolving}
                className="flex-1"
              >
                Continue
              </FetchButton>
            </div>
          </SubPhasePanel>
        )}
      </AnimatePresence>
    </div>
  );
};
